import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../api/client';
import { Icon } from './icons';
import {
  applyPreferences,
  resolveLanguage,
  resolveTheme,
  type Language,
  type Theme,
} from './preferences';

interface PreferencesState {
  theme: Theme;
  language: Language;
  toggleTheme: () => void;
  toggleLanguage: () => void;
  /**
   * Applies what the account already had saved, without writing it back.
   *
   * Called by `AuthProvider` once a session resolves. It is a separate entry
   * point rather than part of the setters because the direction matters: these
   * values *came from* the server, and echoing them straight back would be a
   * pointless write on every page load — and would overwrite a newer choice
   * made from another device in the seconds before this one loaded.
   */
  adopt: (saved: { language: Language | null; theme: Theme | null }) => void;
}

const PreferencesContext = createContext<PreferencesState | null>(null);

/**
 * Language and theme, held above the router.
 *
 * They used to live inside `AppShell`, which meant they did not exist on the
 * sign-in screen — so nobody could switch to Arabic or to the light theme until
 * *after* signing in. For a bilingual product that is backwards: the moment
 * someone most needs the interface in their own language is before they have an
 * account context at all.
 *
 * **Persisted server-side against the account** (FEAT-016 / REQ-088), never in
 * web storage. That is what makes a preference follow the person to another
 * machine, and what stops the shared reception desk deciding which language the
 * next person to sit at it sees.
 *
 * The consequence to keep in mind: before anybody signs in there is nothing to
 * restore, so the sign-in screen uses the browser's language and the dark theme.
 * That is correct rather than a gap — the platform does not know who is looking
 * at it yet.
 */
export function PreferencesProvider({ children }: { children: ReactNode }): ReactElement {
  const { i18n } = useTranslation();

  const [theme, setTheme] = useState<Theme>(() => resolveTheme(null));
  const [language, setLanguage] = useState<Language>(() => resolveLanguage(null));

  useEffect(() => {
    applyPreferences({ theme, language });
  }, [theme, language]);

  useEffect(() => {
    void i18n.changeLanguage(language);
  }, [i18n, language]);

  /*
   * Stable for the life of the provider, deliberately.
   *
   * `AuthProvider` calls this from inside its own `loadUser` callback, and a new
   * function identity on every theme change would re-create that callback and
   * re-run the session-restore effect it feeds — an extra `/auth/refresh` every
   * time somebody toggled dark mode.
   */
  const adopt = useCallback((saved: { language: Language | null; theme: Theme | null }) => {
    if (saved.theme) setTheme(saved.theme);
    if (saved.language) setLanguage(saved.language);
  }, []);

  const value = useMemo<PreferencesState>(
    () => ({
      theme,
      language,

      toggleTheme: () =>
        setTheme((current) => {
          const next = current === 'dark' ? 'light' : 'dark';
          save({ theme: next });
          return next;
        }),

      toggleLanguage: () =>
        setLanguage((current) => {
          const next = current === 'en' ? 'ar' : 'en';
          save({ language: next });
          return next;
        }),

      adopt,
    }),
    [theme, language, adopt],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

/**
 * Records the choice against the account, and never blocks on it.
 *
 * Deliberately fire-and-forget. The toggle has already taken effect on screen,
 * and a slow or failed round trip must not make the interface hesitate — the
 * cost of losing the write is that the preference does not survive a reload,
 * which is exactly where the product was before this existed.
 *
 * A 401 is the ordinary case, not an error: the sign-in screen has these
 * controls too (that is why this provider sits outside `AuthProvider`), and
 * there is no account yet to save them against.
 */
function save(patch: { language?: Language; theme?: Theme }): void {
  void apiRequest('/me/preferences', { method: 'PATCH', body: patch }).catch(() => {
    /* Nobody signed in, or the network blinked. The screen is already correct. */
  });
}

export function usePreferences(): PreferencesState {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used inside a PreferencesProvider.');
  return context;
}

/**
 * The theme and language controls, shared by the sign-in screen and the topbar.
 *
 * One component rather than two copies — the pair appears in both places, and a
 * second implementation is how they drift apart.
 *
 * `withLanguage` is what the two places disagree about, and only that. Once
 * somebody is signed in the language lives in the account menu beside Settings
 * and Sign out, so repeating it here would be two controls for one setting a few
 * pixels apart. Before they sign in there is no menu — and that is exactly when
 * the language switch matters most, because somebody who cannot read the
 * sign-in screen cannot get past it.
 */
export function PreferenceToggles({
  withLanguage = true,
}: {
  withLanguage?: boolean;
} = {}): ReactElement {
  const { t } = useTranslation();
  const { theme, language, toggleTheme, toggleLanguage } = usePreferences();

  const themeLabel = theme === 'dark' ? t('light_mode') : t('dark_mode');

  return (
    <>
      {/* Icon-only, so it carries its own name — the icon itself is aria-hidden. */}
      <button
        type="button"
        className="icon-btn"
        onClick={toggleTheme}
        title={themeLabel}
        aria-label={themeLabel}
      >
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      </button>

      {/*
        A real <button> rather than the prototype's clickable <div>, so it is
        reachable by keyboard. `additions.css` zeroes the browser's default
        button padding, without which the two halves cannot reach the border.
      */}
      {withLanguage ? (
        <button
          type="button"
          className="lang-toggle"
          onClick={toggleLanguage}
          aria-label={language === 'en' ? 'التبديل إلى العربية' : 'Switch to English'}
        >
          <span className={language === 'en' ? 'on' : ''} aria-hidden="true">
            EN
          </span>
          <span className={language === 'ar' ? 'on' : ''} aria-hidden="true">
            ع
          </span>
        </button>
      ) : null}
    </>
  );
}
